import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';
import styles from './StudentDataModal.module.scss';
import AppModal from '@/components/AppModal/AppModal';
import SVG from 'react-inlinesvg';
import { studentDetailsService } from '@/school-management/services/studentDetails';
import { StudentDetails, UnitProgress } from '@/school-management/types/response/studentDetails';
import AppIcon from '@/components/school-management/AppIcon/AppIcon';
import AppLoaderCircle from '@/components/AppLoaderCircle';
import StudentDataHeader from './StudentDataHeader';
import StudentData from './StudentData/StudentData';
import GenerateReport from './GenerateReport/GenerateStudentReport';
import UnitData from './UnitData/UnitData';
import EditContainer, { EditData } from './EditContainer/EditContainer';
import commonUtils from '@/school-management/utils/common';
import pencil from '@/assets/svg/pencil.svg';

export interface StudentDataModalProps {
  schoolId?: string | null;
  studentId?: string | null;
  level?: number | null;
  firstLogin?: string | null;
  onClose?: () => void;
  onEdit?: (studentId: string, newData: EditData, oldData: EditData) => void;
  onDelete?: (studentId: string) => void;
}

const StudentDataModal: React.FC<StudentDataModalProps> = ({
  schoolId,
  studentId,
  level,
  firstLogin,
  onClose,
  onEdit,
  onDelete,
}) => {
  const { t } = useAdminTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [view, setView] = useState<'userData' | 'unitData' | 'edit'>('userData');
  const [data, setData] = useState<StudentDetails | null>(null);
  const [unit, setUnit] = useState<UnitProgress | null>(null);
  const [levelId, setLevelId] = useState<number>(-1);

  useEffect(() => {
    const levelId = level && level > 0 ? level : 0;
    setLevelId(levelId);
  }, [level]);

  useEffect(() => {
    let isActive = true;

    const getStudentDetails = async () => {
      if (schoolId && studentId) {
        setView('userData');
        setUnit(null);
        setIsLoading(true);

        let data: StudentDetails | null = null;

        try {
          data =
            (await studentDetailsService.getStudentDetails(schoolId, studentId, levelId))?.data
              ?.data || null;
        } catch (error) {}

        if (!isActive) {
          return;
        }

        if (data?.progress?.unitsProgress) {
          data.progress.unitsProgress = data.progress.unitsProgress.sort(
            (a, b) => a.order - b.order
          );
        }

        if (data?.progress?.userStreak) {
          data.progress.userStreak = data.progress.userStreak.sort((a, b) => a.order - b.order);
        }

        setData(data);
        setIsLoading(false);
      }
    };

    getStudentDetails();

    return () => {
      isActive = false;
    };
  }, [schoolId, studentId, levelId]);

  const show = useMemo(() => {
    return !!(schoolId && studentId);
  }, [schoolId, studentId]);

  const onBack = useCallback(() => {
    if (view === 'userData') {
      onClose?.();
      return;
    }

    setUnit(null);
    setView('userData');
  }, [view, onClose]);

  const onUnitSelect = useCallback((unitId: number, unitProgresData: UnitProgress) => {
    setUnit(unitProgresData);
    setView('unitData');
  }, []);

  const onShowEditForm = useCallback(() => {
    setView('edit');
  }, []);

  const onStudentDelete = useCallback(() => {
    setView('unitData');
    onClose?.();
    onDelete?.(studentId || '');
  }, [studentId, onClose, onDelete]);

  const onStudentEdit = useCallback(
    (studentId: string, newData: EditData, oldData: EditData) => {
      commonUtils.showToast(t('studentDataModal.editForm.userDataSuccessfullyUpdated'), {
        autoClose: 10000,
        type: 'success',
      });
      setData(data => {
        if (!data) {
          return data;
        }

        return {
          ...data,
          progress: {
            ...(data.progress || {}),
            level: newData.level,
          },
          studentData: {
            ...(data.studentData || {}),
            firstName: newData.firstName,
            lastName: newData.lastName,
            interfaceLanguage: newData.interfaceLanguage,
          },
        };
      });

      if (newData.level !== oldData.level) {
        setLevelId(newData.level);
      }

      onEdit?.(studentId, newData, oldData);
    },
    [onEdit, t]
  );

  const loadingView = (
    <div className={styles.loading}>
      <AppLoaderCircle />
    </div>
  );

  const studentDataHeaderView = <StudentDataHeader data={data} />;

  const userDataView = (
    <div className={styles.userData}>
      <div className={styles.top}>
        {studentDataHeaderView}
        {schoolId &&
          studentId &&
          (data?.progress?.level || 0) > 0 &&
          data?.progress?.unitsProgress?.some(
            unit => unit.hasFailedActivities === true || unit.completedActivities > 0
          ) && (
            <div className={styles.reportBtn}>
              <GenerateReport
                schoolId={schoolId}
                studentId={studentId}
                firstName={data?.studentData?.firstName || ''}
                lastName={data?.studentData?.lastName || ''}
              />
            </div>
          )}
        {data && (
          <button
            className={styles.editBtn}
            onClick={onShowEditForm}
            aria-label={t('studentDataModal.editForm.edit').toString()}
          >
            <SVG src={pencil} />
          </button>
        )}
      </div>
      <div className={styles.body}>
        <StudentData data={data} firstLogin={firstLogin} emitShowUnitData={onUnitSelect} />
      </div>
    </div>
  );

  const unitDataView = (
    <div className={styles.userData}>
      <div className={styles.body}>
        {data && unit && schoolId && studentId && (
          <UnitData studentId={studentId} schoolId={schoolId} studentDetails={data} unit={unit} />
        )}
      </div>
    </div>
  );

  const editView = (
    <div className={styles.userData}>
      <div className={styles.top}>{studentDataHeaderView}</div>
      <div className={styles.body}>
        {schoolId && studentId && data && (
          <EditContainer
            schoolId={schoolId}
            studentId={studentId}
            studentDetails={data}
            emitCloseEdit={onBack}
            emitSaveEdit={onStudentEdit}
            emitDeleteStudent={onStudentDelete}
          />
        )}
      </div>
    </div>
  );

  const content = isLoading
    ? loadingView
    : view === 'userData'
      ? userDataView
      : view === 'unitData'
        ? unitDataView
        : editView;

  return (
    <AppModal
      show={show}
      modalContentClassName={styles.studentDataModal}
      type='slide-horizontal-right'
      blockBodyScroll={true}
    >
      <button className={styles.closeBtn} onClick={onBack}>
        <AppIcon icon='close' />
      </button>
      {content}
      <div className={styles.overLayer} id='StudentDataModalOverLayer'></div>
    </AppModal>
  );
};

export default StudentDataModal;
